import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../../../lib/utils';

// The Messages page's dropdown (Nic 2026-10-21: no old sharp native
// selects). A rounded trigger like the page's inputs and an in-theme menu
// card with a check on the current choice. The menu is portaled and placed
// from the trigger, so a card's overflow-hidden never clips it; it opens
// upward when there is no room below. Keyboard: Enter, Space or the arrows
// open it; the arrows move; Enter picks; Escape closes only the menu.

const GUTTER = 8;
const MENU_MAX = 280;

const Select = ({
  value,
  options, // [{ value, label, hint? }]
  onChange,
  placeholder = 'Pick one',
  disabled = false,
  ariaLabel,
  className = '',
  size = 'md', // md: the page's inputs; sm: compact
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [active, setActive] = useState(-1);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const current = options.find((o) => String(o.value) === String(value)) || null;

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const width = Math.min(Math.max(r.width, 200), vw - GUTTER * 2);
      const left = Math.min(Math.max(GUTTER, r.left), vw - width - GUTTER);
      const below = vh - r.bottom - GUTTER;
      const above = r.top - GUTTER;
      const up = below < Math.min(MENU_MAX, options.length * 36 + 12) && above > below;
      const maxHeight = Math.min(MENU_MAX, (up ? above : below) - 6);
      setPos(up ? { left, width, maxHeight, bottom: vh - r.top + 6 } : { left, width, maxHeight, top: r.bottom + 6 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (!open || active < 0) return;
    menuRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const openMenu = () => {
    if (disabled || !options.length) return;
    setActive(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
    setOpen(true);
  };
  const pick = (o) => {
    setOpen(false);
    btnRef.current?.focus();
    if (String(o.value) !== String(value)) onChange(o.value);
  };
  const onKey = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'Escape') {
      // Only this menu closes, not the layer under it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options[active]) pick(options[active]);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const sm = size === 'sm';
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKey}
        className={cn(
          'flex w-full min-w-0 items-center gap-[8px] rounded-xl bg-white text-left outline-none ring-1 ring-slate-200 transition-[box-shadow] hover:ring-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:cursor-not-allowed disabled:opacity-50 dark:!bg-white/5 dark:ring-white/10 dark:hover:ring-white/20 dark:focus-visible:ring-sky-400/50',
          open && 'ring-2 ring-sky-400 dark:ring-sky-400/50',
          sm ? 'px-[10px] py-[6px] text-[12px] font-semibold text-slate-600 dark:text-slate-300' : 'px-[12px] py-[7px] text-[13px] text-slate-800 dark:text-slate-100',
          className,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !current && 'text-slate-400 dark:text-slate-500')}>
          {current ? current.label : placeholder}
        </span>
        <ChevronDown size={14} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label={ariaLabel}
              onKeyDown={onKey}
              style={pos}
              className="os-scroll fixed z-[1090] overflow-y-auto rounded-xl bg-white p-[4px] shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10"
            >
              {options.map((o, i) => {
                const on = String(o.value) === String(value);
                return (
                  <button
                    key={String(o.value)}
                    type="button"
                    role="option"
                    aria-selected={on}
                    data-i={i}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o)}
                    className={cn(
                      'flex w-full items-center gap-[8px] rounded-lg px-[10px] py-[7px] text-left text-[13px] outline-none focus:outline-none',
                      i === active ? 'bg-slate-100 dark:!bg-white/10' : '',
                      on ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-200',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {o.hint ? <span className="shrink-0 text-[11px] text-slate-400">{o.hint}</span> : null}
                    <Check size={14} className={cn('shrink-0 text-sky-600 dark:text-sky-300', on ? 'opacity-100' : 'opacity-0')} />
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default Select;
